import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../shared/LoadingSpinner';

export default function MFAEnrollPage() {
  const { mfaEnroll, mfaChallenge, mfaVerify } = useAuth();
  const navigate = useNavigate();

  const [factorId, setFactorId] = useState(null);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    (async () => {
      const { data, error: err } = await mfaEnroll();
      if (err) {
        setError(err.message);
        setLoading(false);
        return;
      }
      setFactorId(data.id);
      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setLoading(false);
    })();
  }, []); // eslint-disable-line

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setVerifying(true);

    const { data: challengeData, error: challengeErr } = await mfaChallenge(factorId);
    if (challengeErr) {
      setError(challengeErr.message);
      setVerifying(false);
      return;
    }

    const { error: verifyErr } = await mfaVerify(factorId, challengeData.id, code);
    if (verifyErr) {
      setError('Invalid code. Please try again.');
      setCode('');
      setVerifying(false);
      return;
    }

    navigate('/app', { replace: true });
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary p-4">
      <div className="w-full max-w-[460px]">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.svg" alt="MediLink" className="h-[64px] w-auto mb-4" />
          <h1 className="text-[22px] font-bold text-primary-c">Set up two-factor auth</h1>
          <p className="text-[13px] text-muted-c mt-1">Scan the QR code with your authenticator app</p>
        </div>

        <div className="bg-elevated border border-theme rounded-xl p-6 md:p-7 shadow-lg">
          {/* QR Code */}
          <div className="flex justify-center mb-5">
            <div className="bg-white p-3 rounded-xl">
              <img src={qrCode} alt="MFA QR Code" className="w-[180px] h-[180px]" />
            </div>
          </div>

          {/* Manual entry */}
          <div className="mb-6 text-center">
            <p className="text-[11px] text-muted-c mb-1">Or enter this code manually:</p>
            <code className="text-[13px] font-mono text-accent-light bg-tertiary px-3 py-1.5 rounded-lg select-all">
              {secret}
            </code>
          </div>

          <form onSubmit={handleVerify} className="space-y-4">
            {error && (
              <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium">
                {error}
              </div>
            )}

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Verification Code</label>
              <input
                type="text"
                className={`${inputClass} text-center text-[18px] tracking-[0.3em] font-mono`}
                placeholder="000000"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                autoFocus
                autoComplete="one-time-code"
              />
            </div>

            <button
              type="submit"
              disabled={verifying || code.length !== 6}
              className="w-full py-[11px] rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {verifying ? 'Verifying...' : 'Verify & Enable'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
